<script setup>
defineProps({
  // Not required: omit both when supplying a #header slot instead (e.g.
  // TrainingDetail's page title needs to be a real <h1>, not this
  // component's built-in <h2> — see the #header slot below).
  heading: { type: String, default: '' },
  description: { type: String, default: '' },
})
</script>

<template>
  <div class="split-header-block">
    <div class="header">
      <!-- #header fully replaces the default heading/description markup
           when provided — use it when the caller needs its own heading
           level or extra header-column content (e.g. a facts list). -->
      <slot name="header">
        <h2>{{ heading }}</h2>
        <p v-if="description">{{ description }}</p>
      </slot>
    </div>
    <div class="content">
      <slot />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.split-header-block {
  display: grid;
  grid-template-columns: 1fr;
  gap: $space-6;

  // minmax(0, ...) matters here, not just decoration: a bare `1fr` track
  // won't shrink below its content's min-content width, so two
  // SplitHeaderBlock instances on the same page with different heading/
  // content lengths (e.g. IconListBlock's "Our values" vs LinkCardsBlock's
  // "Learn more about us") could compute slightly different column widths
  // even with the same ratio — the exact "almost lines up" symptom this
  // fixes. ContentSection (the other split-column block) already guards
  // against this the same way.
  //
  // Single column until from-lg (jk-34 — was from-md/768px, with an
  // intermediate 1fr:2fr tier below from-lg; that tier is now gone
  // entirely, going straight from single-column to the narrower 2fr:5fr
  // split): the 768–1023px tablet range was too cramped for a real two-
  // column split once a header/content pair had any real content length.
  // Matches the same single-breakpoint pattern OurApproach.vue/
  // OurWorkDetail.vue's own body-grid already used (both already only
  // switch at from-lg, deliberately mirroring this component's ratio).
  @include from-lg {
    grid-template-columns: minmax(0, 2fr) minmax(0, 5fr);   // header ~2/7
  }
}

.header {
  display: flex;
  flex-direction: column;
  gap: $space-3;

  h2 {
    margin: 0;
    color: $color-ink;
    font-size: $font-size-h2;
    line-height: $lh-heading;
    letter-spacing: $ls-heading;
  }

  p {
    margin: 0;
    font-size: $font-size-lg;
    line-height: $lh-base;
    color: $color-text;
  }
}
</style>
